/**
 * Utility: createRequestFromTemplate
 *
 * Creates a new `request` from a `form_template`, copying all `template_fields`
 * (which now includes type='document' rows) to `request_fields` with remapped IDs
 * and correctly remapped condition references.
 *
 * Simplified Jun 2026: all field types (including 'document') live in template_fields.
 * The form_template_documents table no longer exists. Catalog ID columns removed from
 * request_fields; the field label is the source of truth.
 * Updated Jun 2026: copies short_label from template_fields to request_fields.
 */

import { supabase } from '../lib/supabase';
import { safeParseOptions } from './jsonHelpers';
import { captureError } from './errorReporting';

interface CreateRequestOptions {
  templateId: string;
  userId: string;
  clientId?: string | null;
  sharedLinkId?: string | null;
}

interface CreateRequestResult {
  requestId: string;
  accessToken: string;
}

export async function createRequestFromTemplate(
  options: CreateRequestOptions
): Promise<CreateRequestResult> {
  const { templateId, userId, clientId = null, sharedLinkId = null } = options;

  const { data: template, error: templateError } = await supabase
    .from('form_templates')
    .select('name, description, usage_count')
    .eq('id', templateId)
    .maybeSingle();

  if (templateError) throw templateError;
  if (!template) throw new Error('Template introuvable');

  const { data: fieldsData, error: fieldsError } = await supabase
    .from('template_fields')
    .select('*')
    .eq('template_id', templateId)
    .order('position');

  if (fieldsError) throw fieldsError;

  const { data: request, error: requestError } = await supabase
    .from('requests')
    .insert({
      user_id: userId,
      client_id: clientId,
      template_id: templateId,
      title: template.name,
      description: template.description,
      status: 'sent',
      sent_at: new Date().toISOString(),
      shared_link_id: sharedLinkId,
    })
    .select()
    .single();

  if (requestError) throw requestError;

  const allSourceIds = (fieldsData || []).map((f) => f.id);

  if (allSourceIds.length > 0) {
    const idMapping = new Map<string, string>(
      allSourceIds.map((id) => [id, crypto.randomUUID()])
    );

    const fieldRows = (fieldsData || []).map((field) => ({
      id: idMapping.get(field.id)!,
      request_id: request.id,
      type: field.type,
      label: field.label,
      short_label: field.short_label ?? null,
      description: field.description ?? null,
      is_required: field.is_required,
      position: field.position,
      options: field.options ? safeParseOptions(field.options) : null,
      section: field.type === 'document' ? null : (field.section ?? null),
      is_system: field.is_system ?? false,
      conditions: [] as object[],
      condition_operator: (field.condition_operator as 'or' | 'and') || 'or',
    }));

    const { error: fieldsInsertError } = await supabase
      .from('request_fields')
      .insert(fieldRows);

    if (fieldsInsertError) throw fieldsInsertError;

    const conditionUpdates: Array<{ id: string; conditions: object[] }> = [];

    for (const field of fieldsData || []) {
      const rawConditions = field.conditions;
      if (!Array.isArray(rawConditions) || rawConditions.length === 0) continue;

      const remapped = (
        rawConditions as Array<{ source_field_id: string; values: string[] }>
      )
        .filter((c) => idMapping.has(c.source_field_id))
        .map((c) => ({
          source_field_id: idMapping.get(c.source_field_id)!,
          values: c.values,
        }));

      if (remapped.length > 0) {
        conditionUpdates.push({ id: idMapping.get(field.id)!, conditions: remapped });
      }
    }

    if (conditionUpdates.length > 0) {
      const updateResults = await Promise.all(
        conditionUpdates.map(({ id, conditions }) =>
          supabase.from('request_fields').update({ conditions }).eq('id', id).select('id')
        )
      );
      const failed = updateResults
        .map((res, i) => ({ error: res.error, id: conditionUpdates[i].id }))
        .filter((r) => r.error != null);
      if (failed.length > 0) {
        const details = failed.map((f) => `id=${f.id}: ${f.error!.message}`).join('; ');
        throw new Error(`Failed to update conditions for request_fields: ${details}`);
      }
    }
  }

  const { error: usageCountError } = await supabase.rpc('increment_template_usage', {
    p_template_id: templateId,
  });

  if (usageCountError) {
    console.error(`Failed to increment usage_count for template ${templateId}:`, usageCountError);
    captureError(usageCountError, { util: 'createRequestFromTemplate', action: 'incrementTemplateUsage', templateId });
  }

  return {
    requestId: request.id,
    accessToken: request.access_token,
  };
}
